/* tslint:disable:no-input-prefix */
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, Output, SimpleChange, SimpleChanges, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { BooleanConverter, InputConverter } from '../../../inputConverter';
import { ILoggingService } from '../../../services/interfaces/ILoggingService';
import { LogLevel } from '../../../types';
import { BaseComponent } from '../../base.component';

const instantiateTimeout = 5;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-window-ng',
    styleUrls: ['../../sharedStyles/base.scss', './window.component.scss'],
    templateUrl: './window.component.html'
})
export class WindowComponent extends BaseComponent implements AfterViewInit, OnChanges {

    private _disableKeyboard = false;
    private _height: string | number = null;
    private _isOpened = false;
    private _minHeight: number = 100;
    private _minWidth: number = 200;
    private _width: string | number = null;

    currentHeight: string = '100px';
    currentMaxHeight: string;
    currentMaxWidth: string;
    currentLeft: string;
    currentRight: string;
    currentTop: string;
    currentWidth: string = '200px';
    isInitialized: boolean = false;
    isMaximized: boolean = false;

    get includeGoogleAnalytics() { return false; }

    @Input()
    get windowRef() { return this.modal ? this.modalRef : this.popupRef; }

    @Input()
    get isOpened() { return this._isOpened; }
    set isOpened(open: boolean) {
        this._isOpened = BooleanConverter(open);

        if (!this._isOpened) {
            this.isInitialized = false;
        }

        this.openChange.emit(open);

        const windowStatus = open ? 'Opened' : 'Closed';
        this.LoggingService.Log(`'${this.windowTitle || 'Untitled'}' Window ${windowStatus}`, this.windowRef, LogLevel.Trace);

        this.cdr.detectChanges();
    }

    @Input()
    get disableKeyboard() { return this._disableKeyboard; }
    set disableKeyboard(value: boolean) {
        this._disableKeyboard = BooleanConverter(value);
        this.setKeyboardShortcuts();
    }

    @Input()
    @InputConverter()
    modal: boolean = false;

    @Input()
    @InputConverter()
    isResizable: boolean = false;

    @Input()
    @InputConverter()
    isDraggable: boolean = false;

    @Input()
    @InputConverter()
    isMaximizable: boolean = false;

    @Input()
    @InputConverter()
    closable: boolean = true;

    @Input()
    @InputConverter()
    get minWidth() { return this._minWidth; }
    set minWidth(value: number) {
        this._minWidth = value;
    }

    @Input()
    @InputConverter()
    maxWidth: number = null;

    @Input()
    get width() {
        return this._width === null
            ? ''
            : typeof this._width === 'number' || (typeof this._width === 'string' && this._width.indexOf('px') === -1 && this._width.indexOf('%') === -1)
                ? `${this._width}px`
                : this._width;
    }
    set width(value: string | number) {
        this._width = typeof value === 'string' ? value.replace('px', '') : value;
    }

    @Input()
    @InputConverter()
    get minHeight() { return this._minHeight; }
    set minHeight(value: number) {
        this._minHeight = value;
    }

    @Input()
    @InputConverter()
    maxHeight: number = null;

    @Input()
    get height() {
        return this._height === null
            ? ''
            : typeof this._height === 'number' || (typeof this._width === 'string' && this._width.indexOf('px') === -1 && this._width.indexOf('%') === -1)
                ? `${this._height}px`
                : this._height;
    }
    set height(value: string | number) {
        this._height = typeof value === 'string' ? value.replace('px', '') : value;
    }

    @Input()
    @InputConverter()
    top: number = null;

    @Input()
    @InputConverter()
    left: number = null;

    @Input()
    @InputConverter()
    right: number = null;

    @Input()
    windowTitle: string;

    @Input()
    toggleOpen: boolean = false;

    @ViewChild('modal') private readonly modalRef: HTMLDivElement;
    @ViewChild('popup') private readonly popupRef: HTMLDivElement;
    @ViewChild('resizeBottom') private readonly resizeBottomRef: HTMLDivElement;
    @ViewChild('resizeBottomLeft') private readonly resizeBottomLeftRef: HTMLDivElement;
    @ViewChild('resizeBottomRight') private readonly resizeBottomRightRef: HTMLDivElement;
    @ViewChild('resizeLeft') private readonly resizeLeftRef: HTMLDivElement;
    @ViewChild('resizeRight') private readonly resizeRightRef: HTMLDivElement;
    @ViewChild('resizeTop') private readonly resizeTopRef: HTMLDivElement;
    @ViewChild('resizeTopLeft') private readonly resizeTopLeftRef: HTMLDivElement;
    @ViewChild('resizeTopRight') private readonly resizeTopRightRef: HTMLDivElement;

    @Output() public readonly dragEvent = new EventEmitter<{ left: number; top: number }>();
    @Output() public readonly openChange = new EventEmitter<boolean>();
    @Output() public readonly resizeEvent = new EventEmitter<{ height: number; width: number }>();
    @Output() public readonly stateChange = new EventEmitter<WindowState>();

    constructor(
        _vcr: ViewContainerRef,
        @Inject('LoggingService') public LoggingService: ILoggingService,
        private readonly cdr: ChangeDetectorRef,
        private readonly elementRef: ElementRef<HTMLElement>
    ) {
        super(_vcr);

        this.registerEvent('dragEvent', this.dragEvent);
        this.registerEvent('openChange', this.openChange);
        this.registerEvent('resizeEvent', this.resizeEvent);
        this.registerEvent('stateChange', this.stateChange);
    }

    ngAfterViewInit() {
        this.isReady();

        this.openChange.subscribe(open => {
            if (open) {
                const instantiateInterval = setInterval(() => {
                    if (this.windowRef) {
                        clearInterval(instantiateInterval);
                        if (this.elementRef.nativeElement.title && !this.windowTitle) {
                            this.windowTitle = this.elementRef.nativeElement.title;
                            this.elementRef.nativeElement.removeAttribute('title');
                        }
                        this.setMaxes();
                        this.setKeyboardShortcuts();
                        this.setSize();
                        this.isInitialized = true;
                        this.cdr.detectChanges();
                    }
                }, instantiateTimeout);
            }
        });
    }

    ngOnChanges(changes: IWindowSimpleChanges): void {
        if (changes.maxWidth || changes.maxHeight || changes.minWidth || changes.minHeight) {
            this.setMaxes();
        }

        if (changes.width || changes.height) {
            this.setSize();
        }

        if (changes.toggleOpen) {
            if (changes.toggleOpen.currentValue) {
                this.open();
            }
        }
    }

    @Input() close = () => {
        this.isMaximized = false;
        this.isOpened = false;
    }

    @Input() open = () => {
        this.isMaximized = false;
        this.isOpened = true;
    }

    @Input() toggle = (open?: boolean) => { this.isOpened = open !== undefined ? open : !this.isOpened; };

    setMaxes() {
        if (this.windowRef) {
            const windowDiv = this.getWindowDiv();
            if (windowDiv && !this.modal) {
                this.setPopupMaxes(windowDiv);
            } else if (windowDiv && this.modal) {
                this.setModalMaxes(windowDiv);
            }
        }
    }

    private getWindowDiv(): HTMLDivElement {
        return this.windowRef ? (<any>this.windowRef).nativeElement as HTMLDivElement : null;
    }

    private setPopupMaxes(windowDiv: HTMLDivElement) {
        windowDiv.style.maxHeight = this.maxHeight && this.maxHeight < window.innerHeight ? `${this.maxHeight}px` : `${window.innerHeight}px`;
        windowDiv.style.maxWidth = this.maxWidth && this.maxWidth < window.innerWidth ? `${this.maxWidth}px` : `${window.innerWidth}px`;
        windowDiv.style.minHeight = this.minHeight ? `${this.minHeight}px` : null;
        windowDiv.style.minWidth = this.minWidth ? `${this.minWidth}px` : null;

        if (this.top) {
            windowDiv.style.top = `${this.top}px`;
        } else {
            const height = this.height && this.height !== '' ? Number((this.height as string).replace('px', '')) : windowDiv.getBoundingClientRect().height;
            windowDiv.style.top = `${(window.innerHeight / 2) - (height / 2)}px`;
        }

        if (this.left || this.right) {
            windowDiv.style.left = this.left ? `${this.left}px` : windowDiv.style.left;
            windowDiv.style.right = this.right ? `${this.right}px` : windowDiv.style.right;
        } else {
            const width = this.width && this.width !== '' ? Number((this.width as string).replace('px', '')) : windowDiv.getBoundingClientRect().width;
            windowDiv.style.left = `${(window.innerWidth / 2) - (width / 2)}px`;
        }
    }

    private setModalMaxes(windowDiv: HTMLDivElement) {
        windowDiv.style.maxHeight = null;
        windowDiv.style.maxWidth = null;
        if (!this.height) {
            windowDiv.style.maxHeight = '99vh';
        }
        if (!this.width) {
            windowDiv.style.maxWidth = '99vw';
        }
    }

    private setSize() {
        const windowDiv = this.getWindowDiv();
        if (windowDiv) {
            if (this.height) {
                windowDiv.style.height = this.height as string;
            }
            if (this.width) {
                windowDiv.style.width = this.width as string;
            }
        }
    }

    setKeyboardShortcuts() {
        if (this.windowRef) {
            if (this.disableKeyboard) {
                this.windowRef.tabIndex = null;
            } else {
                this.windowRef.tabIndex = 0;
            }
            this.cdr.detectChanges();
        }
    }

    saveWindowSizeAndLocation() {
        const windowDiv = this.getWindowDiv();
        this.height = windowDiv.style.height;
        this.width = windowDiv.style.width;

        if (windowDiv.style.left !== '') {
            this.left = Number(windowDiv.style.left.replace('px', ''));
        }

        if (windowDiv.style.right !== '') {
            this.right = Number(windowDiv.style.right.replace('px', ''));
        }

        if (windowDiv.style.top !== '') {
            this.top = Number(windowDiv.style.top.replace('px', ''));
        }
    }

    onDrag(mouseDownEvent) {
        if (this.isDraggable && !this.isMaximized) {
            mouseDownEvent = mouseDownEvent || window.event;
            mouseDownEvent.preventDefault();

            let position1 = 0;
            let position2 = 0;
            let position3 = mouseDownEvent.clientX;
            let position4 = mouseDownEvent.clientY;

            const onMouseMove = (mouseMoveEvent) => {
                mouseMoveEvent = mouseMoveEvent || window.event;
                mouseMoveEvent.preventDefault();

                position1 = position3 - mouseMoveEvent.clientX;
                position2 = position4 - mouseMoveEvent.clientY;
                position3 = mouseMoveEvent.clientX;
                position4 = mouseMoveEvent.clientY;

                const windowDiv = this.getWindowDiv();
                windowDiv.style.top = `${(windowDiv.offsetTop - position2)}px`;
                windowDiv.style.left = `${(windowDiv.offsetLeft - position1)}px`;
            };

            const onMouseUp = () => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);

                this.saveWindowSizeAndLocation();

                const windowDiv = this.getWindowDiv();
                this.dragEvent.emit({ left: Number(windowDiv.style.left), top: Number(windowDiv.style.top) });
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        }
    }

    onMaximize() {
        const windowDiv = this.getWindowDiv();
        this.currentHeight = windowDiv.style.height;
        this.currentMaxHeight = windowDiv.style.maxHeight;
        this.currentMaxWidth = windowDiv.style.maxWidth;
        this.currentLeft = windowDiv.style.left;
        this.currentRight = windowDiv.style.right;
        this.currentTop = windowDiv.style.top;
        this.currentWidth = windowDiv.style.width;

        this.isMaximized = true;
        this.cdr.detectChanges();

        windowDiv.style.height = '100vh';
        windowDiv.style.left = '0';
        windowDiv.style.maxHeight = '';
        windowDiv.style.maxWidth = '';
        windowDiv.style.right = '0';
        windowDiv.style.top = '0';
        windowDiv.style.width = '100vw';

        this.stateChange.emit('maximized');
    }

    onRestore() {
        const windowDiv = this.getWindowDiv();
        windowDiv.style.height = this.currentHeight;
        windowDiv.style.left = this.currentLeft;
        windowDiv.style.maxHeight = this.currentMaxHeight;
        windowDiv.style.maxWidth = this.currentMaxWidth;
        windowDiv.style.right = this.currentRight;
        windowDiv.style.top = this.currentTop;
        windowDiv.style.width = this.currentWidth;

        this.isMaximized = false;
        this.cdr.detectChanges();

        this.stateChange.emit('default');
    }

    onResize(mouseDownEvent, resizeElement: HTMLDivElement) {
        mouseDownEvent = mouseDownEvent || window.event;
        mouseDownEvent.preventDefault();

        const canResizeHeight = resizeElement.id === (this.resizeBottomRef as any).nativeElement.id
            || resizeElement.id === (this.resizeBottomLeftRef as any).nativeElement.id
            || resizeElement.id === (this.resizeBottomRightRef as any).nativeElement.id
            || resizeElement.id === (this.resizeTopRef as any).nativeElement.id
            || resizeElement.id === (this.resizeTopLeftRef as any).nativeElement.id
            || resizeElement.id === (this.resizeTopRightRef as any).nativeElement.id;
        const canResizeWidth = resizeElement.id === (this.resizeBottomLeftRef as any).nativeElement.id
            || resizeElement.id === (this.resizeBottomRightRef as any).nativeElement.id
            || resizeElement.id === (this.resizeLeftRef as any).nativeElement.id
            || resizeElement.id === (this.resizeRightRef as any).nativeElement.id
            || resizeElement.id === (this.resizeTopLeftRef as any).nativeElement.id
            || resizeElement.id === (this.resizeTopRightRef as any).nativeElement.id;
        const resizeEventStart = mouseDownEvent;
        const windowDivStartingSize = this.getWindowDiv().getBoundingClientRect();

        const onMouseMove = (mouseMoveEvent) => {
            mouseMoveEvent = mouseMoveEvent || window.event;
            mouseMoveEvent.preventDefault();

            const windowDiv = this.getWindowDiv();

            if (canResizeHeight) {
                const adjustTopPosition = resizeElement.id === (this.resizeTopRef as any).nativeElement.id || resizeElement.id === (this.resizeTopLeftRef as any).nativeElement.id || resizeElement.id === (this.resizeTopRightRef as any).nativeElement.id;
                const newHeight = adjustTopPosition
                    ? resizeEventStart.y - mouseMoveEvent.y
                    : mouseMoveEvent.y - resizeEventStart.y;
                const heightAdjustment = windowDivStartingSize.height + newHeight;

                if (heightAdjustment >= this.minHeight && (!this.maxHeight || (this.maxHeight && heightAdjustment <= this.maxHeight))) {
                    windowDiv.style.height = `${heightAdjustment}px`;

                    if (adjustTopPosition) {
                        windowDiv.style.top = `${mouseMoveEvent.y}px`;
                    }
                }
            }

            if (canResizeWidth) {
                const adjustLeftPosition = resizeElement.id === (this.resizeBottomLeftRef as any).nativeElement.id || resizeElement.id === (this.resizeLeftRef as any).nativeElement.id || resizeElement.id === (this.resizeTopLeftRef as any).nativeElement.id;
                const newWidth = adjustLeftPosition
                    ? resizeEventStart.x - mouseMoveEvent.x
                    : mouseMoveEvent.x - resizeEventStart.x;
                const widthAdjustment = windowDivStartingSize.width + newWidth;

                if (widthAdjustment >= this.minWidth && (!this.maxWidth || (this.maxWidth && widthAdjustment <= this.maxWidth))) {
                    windowDiv.style.width = `${widthAdjustment}px`;

                    if (adjustLeftPosition) {
                        windowDiv.style.left = `${mouseMoveEvent.x}px`;
                    }
                }
            }
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);

            this.saveWindowSizeAndLocation();

            const windowDiv = this.getWindowDiv().getBoundingClientRect();
            this.resizeEvent.emit({ height: windowDiv.height, width: windowDiv.width });
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }
}
interface IWindowSimpleChanges extends SimpleChanges {
    width: SimpleChange;
    height: SimpleChange;
    maxWidth: SimpleChange;
    maxHeight: SimpleChange;
    minWidth: SimpleChange;
    minHeight: SimpleChange;
}

export type WindowState = 'default' | 'maximized' | 'minimized';
